/**
 * CategoryRevenueWidget.jsx
 * Dashboard-Widget: Umsatz nach Leistungsart
 * Für Admin/Geschäftsführung/Buchhaltung
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip, 
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import { FolderTree, TrendingUp, Clock, Euro, BarChart3, PieChartIcon, RefreshCcw } from 'lucide-react';

// Default colors for categories without custom color
const DEFAULT_COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', 
  '#EC4899', '#14B8A6', '#F97316', '#84CC16', '#6366F1'
];

export default function CategoryRevenueWidget() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('year'); // 'year' or 'month'
  const [viewMode, setViewMode] = useState('amount'); // 'amount' or 'hours'
  const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'

  // Calculate date range based on period
  const getDateRange = useCallback(() => {
    const now = new Date();
    if (period === 'year') {
      return {
        date_from: `${now.getFullYear()}-01-01`,
        date_to: now.toISOString().split('T')[0]
      };
    } else {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      return {
        date_from: `${year}-${month}-01`,
        date_to: `${year}-${month}-${lastDay}`
      };
    }
  }, [period]);

  // Only show for admin/accounting/supervisor
  const hasAccess = ['admin', 'accounting', 'supervisor'].includes(user?.role);
  
  // Fetch data - only if has access
  const fetchData = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      const range = getDateRange();
      const res = await api.get('/reports/by-category', {
        params: {
          date_from: range.date_from,
          date_to: range.date_to,
          group: 'main'
        }
      });
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch category data:', error);
    } finally {
      setLoading(false);
    }
  }, [api, getDateRange, hasAccess]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!hasAccess) {
    return null;
  }

  // Prepare chart data
  const chartData = data?.categories?.map((cat, index) => ({
    name: cat.category_name,
    value: viewMode === 'amount' ? cat.billable_amount : cat.hours,
    color: cat.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
    billable_hours: cat.billable_hours,
    non_billable_hours: cat.non_billable_hours,
    billable_amount: cat.billable_amount,
    hours: cat.hours,
    percent: viewMode === 'amount' ? cat.amount_percent : cat.hours_percent
  })) || [];

  // Add uncategorized if significant
  if (data?.uncategorized?.hours > 0) {
    chartData.push({
      name: 'Ohne Kategorie',
      value: viewMode === 'amount' ? data.uncategorized.billable_amount : data.uncategorized.hours,
      color: '#6B7280',
      billable_hours: data.uncategorized.billable_hours,
      non_billable_hours: data.uncategorized.non_billable_hours,
      billable_amount: data.uncategorized.billable_amount,
      hours: data.uncategorized.hours,
      percent: data.uncategorized.hours_percent
    });
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{d.name}</p>
          <div className="space-y-1 text-sm">
            <p className="text-muted-foreground">
              <span className="text-foreground">{d.hours?.toFixed(1)}h</span> gesamt
            </p>
            <p className="text-muted-foreground">
              <span className="text-lime-600 dark:text-lime-400">{d.billable_hours?.toFixed(1)}h</span> abrechenbar
            </p>
            <p className="text-muted-foreground">
              <span className="text-muted-foreground">{d.non_billable_hours?.toFixed(1)}h</span> nicht abr.
            </p>
            <p className="text-muted-foreground">
              <span className="text-emerald-600 dark:text-emerald-400">{d.billable_amount?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
            </p>
            <p className="text-muted-foreground text-xs">
              {d.percent?.toFixed(1)}% {viewMode === 'amount' ? 'vom Umsatz' : 'der Stunden'}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const formatValue = (value) => {
    if (viewMode === 'amount') {
      return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
    }
    return `${value.toFixed(0)}h`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-glass p-6"
      data-testid="category-revenue-widget"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
            <FolderTree className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-foreground">Umsatz nach Leistungsart</h3>
            <p className="text-sm text-muted-foreground">
              {period === 'year' ? 'Laufendes Jahr' : 'Laufender Monat'}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Toggle */}
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setPeriod('year')}
              className={`px-3 py-1.5 text-sm ${period === 'year' ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground hover:bg-muted'}`}
            >
              Jahr
            </button>
            <button
              onClick={() => setPeriod('month')}
              className={`px-3 py-1.5 text-sm ${period === 'month' ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground hover:bg-muted'}`}
            >
              Monat
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setViewMode('amount')}
              className={`px-3 py-1.5 text-sm flex items-center gap-1 ${viewMode === 'amount' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <Euro className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('hours')}
              className={`px-3 py-1.5 text-sm flex items-center gap-1 ${viewMode === 'hours' ? 'bg-lime-500/20 text-lime-400' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <Clock className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Chart Type Toggle */}
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setChartType('pie')}
              className={`px-3 py-1.5 text-sm ${chartType === 'pie' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <PieChartIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1.5 text-sm ${chartType === 'bar' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="h-64 flex items-center justify-center">
          <RefreshCcw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* No Data */}
      {!loading && chartData.length === 0 && (
        <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
          <FolderTree className="w-12 h-12 mb-3 opacity-50" />
          <p>Keine Daten für diesen Zeitraum</p>
        </div>
      )}

      {/* Chart */}
      {!loading && chartData.length > 0 && (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'pie' ? (
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${percent.toFixed(0)}%)`}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            ) : (
              <BarChart data={chartData} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis type="number" stroke="#71717a" tickFormatter={formatValue} />
                <YAxis type="category" dataKey="name" stroke="#71717a" width={100} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Totals */}
      {!loading && data?.totals && (
        <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Gesamt Stunden</p>
            <p className="text-lg font-mono font-bold text-foreground">{data.totals.total_hours?.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Abrechenbar</p>
            <p className="text-lg font-mono font-bold text-lime-600 dark:text-lime-400">{data.totals.billable_hours?.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Nicht abrechenbar</p>
            <p className="text-lg font-mono font-bold text-muted-foreground">{data.totals.non_billable_hours?.toFixed(1)}h</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Umsatz (abr.)</p>
            <p className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">
              {data.totals.billable_amount?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}
